import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Heart, Sparkles, MessageSquareQuote, Users, Search, MapPin, ArrowRight, Inbox } from "lucide-react";

/* ---------------- Brivio location store (localStorage + live event) ---------------- */
const LOC_KEY = "brivio_location_v1";
const LOC_EVENT = "brivio-location-change";

export const getBrivioLocation = () => {
  try { return JSON.parse(localStorage.getItem(LOC_KEY) || "null"); } catch { return null; }
};
export const setBrivioLocation = (loc) => {
  try { localStorage.setItem(LOC_KEY, JSON.stringify(loc)); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(LOC_EVENT, { detail: loc }));
};
export const useBrivioLocation = () => {
  const [loc, setLoc] = useState(getBrivioLocation);
  useEffect(() => {
    const h = (e) => setLoc(e.detail || getBrivioLocation());
    window.addEventListener(LOC_EVENT, h);
    return () => window.removeEventListener(LOC_EVENT, h);
  }, []);
  const update = useCallback((v) => { setBrivioLocation(v); }, []);
  return [loc, update];
};

/* ---------------- Layout primitives ---------------- */
export const Container = ({ className = "", children }) => (
  <div className={`max-w-[1200px] mx-auto px-5 md:px-10 ${className}`}>{children}</div>
);

export const SectionHeading = ({ eyebrow, title, sub, center = false, className = "" }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : ""} ${className}`}>
    {eyebrow && <span className="text-[#16A34A] font-semibold uppercase tracking-wider text-xs">{eyebrow}</span>}
    <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0D1B3D] mt-2">{title}</h2>
    {sub && <p className="mt-3 text-base md:text-lg text-[#64748B] leading-relaxed">{sub}</p>}
  </div>
);

/* ---------------- Safe notice (beta payment / privacy wording) ---------------- */
export const SafeNoticeCard = ({ icon: Icon = ShieldCheck, children, className = "", testid = "safe-notice" }) => (
  <div data-testid={testid} className={`flex items-start gap-3 rounded-2xl bg-[#EFF6FF] border border-[#DBEAFE] px-4 py-3.5 ${className}`}>
    <Icon className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
    <p className="text-sm text-[#1E40AF] leading-relaxed">{children}</p>
  </div>
);

/* ---------------- Step card (How it works) ---------------- */
const STEP_ACCENT = {
  green: { ring: "bg-[#DCFCE7] text-[#16A34A]", num: "bg-[#16A34A]" },
  blue: { ring: "bg-[#DBEAFE] text-[#2563EB]", num: "bg-[#2563EB]" },
};
export const StepCard = ({ num, icon: Icon, title, desc, tip, accent = "green", testid }) => {
  const a = STEP_ACCENT[accent] || STEP_ACCENT.green;
  return (
    <div data-testid={testid} className="relative bg-white rounded-[20px] border border-[#E5EAF3] p-6 shadow-[0_8px_24px_rgba(15,23,42,0.04)] h-full flex flex-col">
      <span className={`absolute top-4 left-4 w-6 h-6 rounded-full ${a.num} text-white text-xs font-bold flex items-center justify-center`}>{num}</span>
      <div className={`w-14 h-14 rounded-full ${a.ring} flex items-center justify-center mx-auto mt-2 mb-4`}><Icon className="w-7 h-7" /></div>
      <h3 className="font-display text-base font-bold text-[#0D1B3D] text-center">{title}</h3>
      <p className="mt-1.5 text-sm text-[#64748B] text-center leading-relaxed">{desc}</p>
      {tip && (
        <div className="mt-4 rounded-xl bg-[#F0FDF4] px-3 py-2 flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-[#16A34A] shrink-0 mt-0.5" />
          <span className="text-xs text-[#166534] leading-snug">{tip}</span>
        </div>
      )}
    </div>
  );
};

/* ---------------- Trust strip ---------------- */
const DEFAULT_TRUST = [
  { icon: ShieldCheck, tint: "bg-[#DCFCE7] text-[#16A34A]", title: "Nearby Providers", body: "Find service providers near your area." },
  { icon: Lock, tint: "bg-[#DBEAFE] text-[#2563EB]", title: "Privacy First", body: "Your information is protected and shared only as needed to manage your job." },
  { icon: Users, tint: "bg-[#EDE9FE] text-[#7C3AED]", title: "Built for Local Jobs", body: "Brivio helps customers and nearby providers connect, quote, and manage jobs." },
  { icon: Heart, tint: "bg-[#FFEDD5] text-[#F97316]", title: "Always Improving", body: "We listen to your feedback and keep making Brivio better." },
];
export const TrustStrip = ({ items = DEFAULT_TRUST, testid = "trust-strip" }) => (
  <div data-testid={testid} className="rounded-[24px] bg-[#F8FAFC] border border-[#E5EAF3] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 md:p-8">
    {items.map((it) => (
      <div key={it.title} className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${it.tint}`}><it.icon className="w-5 h-5" /></div>
        <div className="min-w-0">
          <p className="font-display font-bold text-[#0D1B3D]">{it.title}</p>
          <p className="text-sm text-[#64748B] leading-snug mt-0.5">{it.body}</p>
        </div>
      </div>
    ))}
  </div>
);

/* ---------------- CTA section ---------------- */
export const CTASection = ({
  title = "Ready to post a request?",
  sub = "Tell us what you need and get quotes from nearby providers.",
  primaryLabel = "Post a Request Free", primaryTo = "/b2/request",
  secondaryLabel = "Nearby Providers", secondaryTo = "/search",
  testid = "cta-section",
}) => {
  const navigate = useNavigate();
  return (
    <div data-testid={testid} className="rounded-[24px] bg-[#EFF4FF] border border-[#DBEAFE] px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0D1B3D]">{title}</h2>
        <p className="text-[#475569] mt-1.5">{sub}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 shrink-0">
        <button data-testid={`${testid}-secondary`} onClick={() => navigate(secondaryTo)}
          className="inline-flex items-center justify-center gap-2 bg-white border border-[#CBD5E1] text-[#0D1B3D] hover:border-[#2563EB] hover:text-[#2563EB] rounded-[16px] px-6 py-3 font-semibold transition-colors">
          <Search className="w-4 h-4" /> {secondaryLabel}
        </button>
        <button data-testid={`${testid}-primary`} onClick={() => navigate(primaryTo)}
          className="inline-flex items-center justify-center gap-2 bg-[var(--b2-green)] text-white hover:bg-[var(--b2-green-hover)] rounded-[16px] px-6 py-3 font-semibold transition-colors shadow-[var(--b2-shadow-cta)]">
          {primaryLabel} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ---------------- Empty state ---------------- */
export const EmptyState = ({ icon: Icon = Inbox, title, sub, testid = "empty-state" }) => (
  <div data-testid={testid} className="rounded-[24px] border border-dashed border-[#CBD5E1] bg-white px-6 py-16 text-center">
    <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] text-[#94A3B8] flex items-center justify-center mx-auto mb-4"><Icon className="w-7 h-7" /></div>
    <h3 className="font-display text-lg font-bold text-[#0D1B3D]">{title}</h3>
    {sub && <p className="text-sm text-[#64748B] mt-1.5 max-w-md mx-auto">{sub}</p>}
  </div>
);

/* ---------------- Status badge (areas) ---------------- */
const STATUS_MAP = {
  active: { label: "Active Beta", cls: "bg-[#DCFCE7] text-[#166534]" },
  auto_launched: { label: "Active Beta", cls: "bg-[#DCFCE7] text-[#166534]" },
  growing: { label: "Growing", cls: "bg-[#FEF3C7] text-[#92400E]" },
  building: { label: "Growing", cls: "bg-[#FEF3C7] text-[#92400E]" },
  coming: { label: "Coming soon", cls: "bg-[#E2E8F0] text-[#475569]" },
  disabled: { label: "Paused", cls: "bg-[#F1F5F9] text-[#94A3B8]" },
};
export const StatusBadge = ({ status, testid }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.coming;
  return <span data-testid={testid} className={`text-xs font-semibold rounded-full px-2.5 py-1 ${s.cls}`}>{s.label}</span>;
};

export const TrustIcons = { ShieldCheck, Lock, Heart, MessageSquareQuote, Users, MapPin, Sparkles };
